// News Forum — service-education articles. One entry per article; each gets its own page at /news/:slug.
import { uns } from "@/lib/uns";

const img = (id) => {
  const { src, srcSet } = uns(id, { w: 1400, widths: [480, 800, 1200, 1600] });
  return { image: src, imageSrcSet: srcSet };
};

export const newsCategories = [
  { id: "suspension", label: "Steering & Suspension", blurb: "Shocks, struts, ball joints, alignment, and the Birmingham pothole problem." },
  { id: "ac", label: "Air Conditioning", blurb: "Recharge, leak detection, compressors, and surviving Alabama summers." },
  { id: "brakes", label: "Brakes", blurb: "Pads, rotors, calipers, and the sounds that mean stop now." },
  { id: "diagnostics", label: "Diagnostics", blurb: "Check engine lights, codes, and what a real diagnosis looks like." },
  { id: "oil", label: "Oil & Maintenance", blurb: "Oil types, intervals, filters, and the small stuff that saves engines." },
  { id: "tires", label: "Tires & Alignment", blurb: "Rotation, balancing, alignment, and reading your tread." },
  { id: "electrical", label: "Electrical & Battery", blurb: "Batteries, alternators, starters, and no-start mornings." },
  { id: "cooling", label: "Cooling System", blurb: "Radiators, coolant, thermostats, and overheating." },
];

export const categoryById = (id) => newsCategories.find((c) => c.id === id);

export const articles = [
  {
    slug: "signs-your-suspension-needs-attention",
    category: "suspension",
    title: "Signs Your Suspension Needs Attention",
    subtitle: "Shocks, struts, and the Birmingham pothole problem",
    excerpt:
      "Bouncy ride, nose-diving when you brake, uneven tire wear — your suspension is talking. Here's how to listen before a $75 inspection turns into a $1,200 repair.",
    date: "2026-06-02",
    readTime: "5 min",
    author: "Auto Basic Care Service Team",
    ...img("photo-1613214149922-f1809c99b414"),
    imageAlt: "Technician removing a wheel to inspect the suspension and brakes",
    bookingService: "Suspension Inspection",
    featured: true,
    body: [
      { t: "p", c: "Your suspension does a lot more than keep the ride smooth. It keeps all four tires planted on the road so you can steer, brake, and corner predictably. When it starts to wear, the first symptoms are subtle — which is exactly why so many drivers around Birmingham don't notice until something fails." },
      { t: "h2", c: "The five warning signs we see most" },
      { t: "ul", c: [
        "Nose-dive when braking — the front end dips hard when you stop. Worn front shocks or struts can't control the weight transfer.",
        "Bounce test failure — push down hard on a fender and let go. One rebound is normal. Two or three means the damper is done.",
        "Drifting or pulling in turns — the body rolls more than it used to, or the car feels 'loose' on the interstate.",
        "Uneven or cupped tire wear — scalloped patches around the tread are a classic sign the tire is bouncing instead of rolling.",
        "Clunks, knocks, or creaks over bumps — usually a ball joint, sway bar link, or strut mount that has developed play.",
      ] },
      { t: "h2", c: "Why Birmingham roads are hard on suspension" },
      { t: "p", c: "Between freeze-thaw cycles in winter, heavy summer rain, and the constant construction around I-65 and I-20/59, our roads develop potholes fast. A single hard hit can bend a control arm or blow a strut seal. Repeated smaller hits wear out bushings and tie rod ends years earlier than the manufacturer planned." },
      { t: "tip", c: "If you hit a pothole hard enough to make you wince, get the suspension and alignment checked within a week or two. Small damage caught early is cheap. Ignored, it eats tires and other components." },
      { t: "h2", c: "What a suspension inspection actually covers" },
      { t: "p", c: "Our $75 Suspension Inspection puts the vehicle on the lift and checks every shock and strut for leaks and play, every ball joint and tie rod end for movement, all bushings for cracking, and the springs for sag or breakage. We also measure tire wear patterns, which tell us a lot about how the vehicle has been riding. You get a written report with photos and a clear list of what's urgent, what's coming, and what's fine." },
      { t: "h2", c: "Repair or replace?" },
      { t: "p", c: "Shocks and struts are wear items — they don't get repaired, they get replaced, and always in pairs (both front or both rear) so the vehicle stays balanced. Ball joints, tie rods, and sway bar links are replaced individually as they wear. Any time we replace steering or suspension parts, we recommend an alignment afterward to protect your tires." },
    ],
  },
  {
    slug: "struts-vs-shocks-whats-the-difference",
    category: "suspension",
    title: "Struts vs. Shocks: What's the Difference?",
    subtitle: "And how to know when it's time to replace them",
    excerpt:
      "People use the words interchangeably, but shocks and struts are different parts with different jobs — and different price tags. A quick explainer from our lift.",
    date: "2026-05-19",
    readTime: "4 min",
    author: "Auto Basic Care Service Team",
    ...img("photo-1615906655593-ad0386982a0f"),
    imageAlt: "Mechanic working on a vehicle suspension component in the shop",
    bookingService: "Suspension Inspection",
    body: [
      { t: "p", c: "Every vehicle has either shocks or struts at each corner — sometimes struts up front and shocks in the rear. Both dampen the spring's movement so your car doesn't keep bouncing after a bump. The difference is structural." },
      { t: "h2", c: "Shocks" },
      { t: "p", c: "A shock absorber is a standalone damper. It bolts between the body and the suspension arm and does one job: control the spring. It does not hold the vehicle up and it isn't part of the steering geometry. Shocks are typically less expensive to replace and don't always require an alignment afterward." },
      { t: "h2", c: "Struts" },
      { t: "p", c: "A strut is a structural part of the suspension. It combines the damper with the coil spring and acts as the upper pivot point for steering. Because the strut sets the wheel's camber and caster, replacing a strut changes the alignment — which is why we always align a vehicle after strut work." },
      { t: "h2", c: "How long do they last?" },
      { t: "p", c: "Most manufacturers suggest inspecting dampers at 50,000 miles and expecting replacement somewhere between 50,000 and 100,000 miles. Birmingham's roads, towing, or a lot of highway miles will shorten that. If your vehicle has more than 75,000 miles on original dampers, it's worth a look." },
      { t: "ul", c: [
        "Visible fluid leaking down the damper body",
        "A 'floaty' or unsettled feeling at highway speed",
        "Longer stopping distances — worn dampers can add 10–20% to braking distance",
        "Cupped tire wear or a rhythmic tire noise",
      ] },
      { t: "tip", c: "Always replace shocks or struts in pairs. A new damper on one side and a tired one on the other will make the vehicle handle unevenly — and can be dangerous in an emergency stop." },
      { t: "p", c: "Not sure which you have or whether they're worn? Book a Suspension Inspection and we'll show you on the lift." },
    ],
  },
  {
    slug: "why-is-my-car-ac-blowing-warm-air",
    category: "ac",
    title: "Why Is My Car AC Blowing Warm Air?",
    subtitle: "Six common causes — and which ones are quick fixes",
    excerpt:
      "Warm air in June is miserable. Sometimes it's a $150 recharge. Sometimes it's a compressor. Here's how we tell the difference, and what you can check yourself.",
    date: "2026-06-09",
    readTime: "6 min",
    author: "Auto Basic Care Service Team",
    ...img("photo-1504222490345-c075b6008014"),
    imageAlt: "Technician servicing a vehicle with the hood raised",
    bookingService: "AC Inspection",
    featured: true,
    body: [
      { t: "p", c: "When your AC stops blowing cold, the system is trying to tell you something specific. A good diagnosis finds the cause instead of just topping off refrigerant and hoping. These are the six causes we see most often in the Birmingham heat." },
      { t: "h2", c: "1. Low refrigerant (a leak)" },
      { t: "p", c: "AC systems are sealed. If refrigerant is low, it leaked out — it doesn't get 'used up.' Small leaks at O-rings and Schrader valves are common and cheap to fix. Our AC Recharge includes a UV dye and leak check so we're not just refilling a bucket with a hole in it." },
      { t: "h2", c: "2. Failing compressor or clutch" },
      { t: "p", c: "The compressor is the pump that pressurizes refrigerant. If the clutch won't engage or the compressor is worn internally, you'll hear a click but get no cold air — or hear a grinding noise when the AC is on. This is the most expensive AC repair, so we confirm it carefully before recommending it." },
      { t: "h2", c: "3. Condenser blockage or damage" },
      { t: "p", c: "The condenser sits in front of the radiator and sheds heat from the refrigerant. Road debris, bugs, or a minor front-end bump can block or puncture it. Symptom: AC works at highway speed but goes warm in traffic." },
      { t: "h2", c: "4. Cooling fan not running" },
      { t: "p", c: "If the electric fan behind the radiator isn't running when the AC is on, the condenser can't cool at idle. Same symptom as a blocked condenser — cold on the interstate, warm at a red light." },
      { t: "h2", c: "5. Blend door or control problem" },
      { t: "p", c: "Sometimes the AC is making cold air perfectly — but a stuck blend door actuator is mixing in heat. The giveaway is a clicking noise behind the dash or one side of the car cooler than the other." },
      { t: "h2", c: "6. Clogged cabin air filter" },
      { t: "p", c: "Not technically an AC failure, but a filthy cabin filter chokes airflow so badly that cold air barely reaches you. It's a $45 fix and one of the first things we check." },
      { t: "tip", c: "Run your AC for at least 10 minutes once a week, even in winter. It keeps the compressor seals lubricated and prevents the slow leaks that leave you sweating in May." },
      { t: "p", c: "Our $95 AC Inspection measures system pressures, checks compressor operation, inspects for leaks, and verifies airflow. If a recharge is all you need, the inspection fee applies toward it." },
    ],
  },
  {
    slug: "ac-recharge-vs-ac-repair",
    category: "ac",
    title: "AC Recharge vs. AC Repair",
    subtitle: "What Alabama summers do to your system, and when a recharge isn't enough",
    excerpt:
      "A recharge is the right call about half the time. The other half, it's money down the drain. Here's how to know which situation you're in before you spend a dime.",
    date: "2026-05-26",
    readTime: "4 min",
    author: "Auto Basic Care Service Team",
    ...img("photo-1606016159991-dfe4f2746ad5"),
    imageAlt: "Car driving down a sunlit highway in summer",
    bookingService: "AC Recharge",
    body: [
      { t: "p", c: "'Can you just recharge it?' is the most common AC question we get. The honest answer is: only if it's low, and only if we know why it's low. Here's how we think about it." },
      { t: "h2", c: "When a recharge makes sense" },
      { t: "ul", c: [
        "The system is slightly low after several years — a tiny amount of seepage through hoses is normal over 5–8 years.",
        "The compressor engages, the fan runs, and pressures are just a little under spec.",
        "A leak check finds nothing significant, or finds a small, easily fixed O-ring leak.",
      ] },
      { t: "h2", c: "When a recharge is a waste of money" },
      { t: "ul", c: [
        "The system is empty. Empty means a real leak — a recharge will last days or weeks.",
        "The compressor won't engage even with correct pressure. That's electrical or mechanical, not refrigerant.",
        "There's moisture or contamination in the system — it has to be evacuated and the receiver/drier replaced.",
      ] },
      { t: "h2", c: "Why DIY cans can make it worse" },
      { t: "p", c: "Parts-store recharge cans have no way to measure how much refrigerant is already in the system. Overcharging raises pressures, makes the AC blow warmer, and can damage the compressor. Many cans also include 'stop leak' additives that gum up the expansion valve and cost more to clean out than the original leak." },
      { t: "h2", c: "What our AC Recharge includes" },
      { t: "p", c: "We recover any remaining refrigerant, pull a vacuum to remove moisture, add UV leak-detection dye, recharge to the exact factory weight, and verify vent temperatures. It's $150 for most vehicles and takes about an hour. If we find a leak that needs parts, we'll show you and quote it before doing anything else." },
      { t: "tip", c: "Book AC service in spring, before the first 95° week. Every shop in Birmingham is slammed with AC work in June and July." },
    ],
  },
  {
    slug: "how-to-know-when-brake-pads-are-worn",
    category: "brakes",
    title: "How to Know When Your Brake Pads Are Worn",
    subtitle: "The sounds, feelings, and measurements that matter",
    excerpt:
      "Squeal, grind, pulse, pull. Every brake symptom points somewhere. Learn what each one means and how far you can safely drive before getting it looked at.",
    date: "2026-05-12",
    readTime: "5 min",
    author: "Auto Basic Care Service Team",
    ...img("photo-1580273916550-e323be2ae537"),
    imageAlt: "Close-up of a brake rotor and caliper on a lifted vehicle",
    bookingService: "Brake Inspection",
    body: [
      { t: "p", c: "Brake pads are designed to wear out — that's how they work. The trick is replacing them before they wear into the rotors, which turns a pad job into a pads-and-rotors job." },
      { t: "h2", c: "Listen" },
      { t: "ul", c: [
        "High-pitched squeal when braking: usually the built-in wear indicator. You have some time, but book an inspection.",
        "Metallic grinding: the pad material is gone and metal is on metal. Stop driving on it — every mile is damaging the rotors.",
        "Clicking or rattling: loose hardware or a pad shifting in the bracket.",
      ] },
      { t: "h2", c: "Feel" },
      { t: "ul", c: [
        "Pulsing in the pedal or steering wheel: warped or unevenly worn rotors.",
        "Pulling to one side: a sticking caliper or a collapsed brake hose on one side.",
        "Soft or spongy pedal: air or moisture in the fluid — this is a safety issue, get it checked right away.",
        "Pedal goes further than it used to: pads are thin, or fluid is low.",
      ] },
      { t: "h2", c: "Measure" },
      { t: "p", c: "New pads have about 10–12 mm of friction material. Manufacturers call for replacement around 3 mm. Anything under 2 mm is unsafe. Our $45 Brake Inspection measures every pad and rotor with a gauge and gives you the numbers — not just 'looks fine' or 'needs brakes.'" },
      { t: "tip", c: "Most drivers get 30,000–70,000 miles from a set of pads. Stop-and-go city driving, towing, and hilly routes wear them faster. Have them measured at every oil change so you're never surprised." },
      { t: "p", c: "Right now our Brake Service Special is $119 per axle for premium pads, rotor inspection, full system inspection, and a road test. If the rotors are still in spec, that's all you need." },
    ],
  },
  {
    slug: "check-engine-light-what-it-means",
    category: "diagnostics",
    title: "Check Engine Light On? Here's What It Actually Means",
    subtitle: "Steady vs. flashing, why a free code scan isn't a diagnosis, and what to do next",
    excerpt:
      "The light is a symptom, not a diagnosis. A code tells you which system complained — not why. Here's how real diagnostics work and why it saves you money.",
    date: "2026-04-28",
    readTime: "5 min",
    author: "Auto Basic Care Service Team",
    ...img("photo-1486262715619-67b85e0b08d3"),
    imageAlt: "Open engine bay of a modern vehicle",
    bookingService: "Check Engine Light Diagnostics",
    body: [
      { t: "p", c: "The check engine light comes on when your vehicle's computer sees a reading outside the range it expects. That's it. It doesn't know if the part is bad, a sensor is bad, a wire is corroded, or there's a vacuum leak upstream making a good sensor report bad numbers." },
      { t: "h2", c: "Steady vs. flashing" },
      { t: "p", c: "A steady light means 'get this checked soon.' You can usually keep driving. A flashing light means an active misfire that can overheat and destroy the catalytic converter — a $1,000+ part — in minutes. Pull over safely and have the vehicle towed or drive very gently straight to a shop." },
      { t: "h2", c: "Why the free parts-store scan isn't enough" },
      { t: "p", c: "A code like P0420 (catalyst efficiency) gets thousands of catalytic converters replaced every year that didn't need it. The real cause is often an exhaust leak, a lazy oxygen sensor, or a misfire. The code points at the symptom. Diagnosis finds the cause." },
      { t: "h2", c: "What our diagnostic includes" },
      { t: "ul", c: [
        "Full scan of all modules, not just the engine — plus freeze-frame data showing conditions when the fault set",
        "Live data review to see what sensors are reporting right now",
        "Targeted tests for the suspect circuit: voltage, ground, smoke test for vacuum leaks, fuel pressure, etc.",
        "A written explanation of what failed, why, and what it costs to fix — before any repair",
      ] },
      { t: "tip", c: "A loose gas cap can set an evaporative emissions code. If the light comes on right after fueling up, tighten the cap and drive a few days. If it stays on, come see us." },
      { t: "p", c: "Check Engine Light Diagnostics is $125 and typically takes about an hour. If you approve the repair with us, that fee is applied to the work." },
    ],
  },
  {
    slug: "conventional-vs-synthetic-oil",
    category: "oil",
    title: "Conventional vs. Synthetic Oil: Which Does Your Engine Need?",
    subtitle: "Three tiers, three price points, one right answer for your vehicle",
    excerpt:
      "Synthetic isn't always 'better' — it's better for certain engines and driving. Here's how to pick the right tier without overpaying or underprotecting.",
    date: "2026-04-14",
    readTime: "4 min",
    author: "Auto Basic Care Service Team",
    ...img("photo-1487754180451-c456f719a1fc"),
    imageAlt: "Technician replacing an oil filter under the hood",
    bookingService: "Oil Change",
    body: [
      { t: "p", c: "Motor oil has one job that matters more than any other: keep metal from touching metal. All three tiers do that. The difference is how long they do it well, and under what conditions." },
      { t: "h2", c: "Conventional ($49.99)" },
      { t: "p", c: "Refined from crude oil with additive packages. Perfectly good for older, non-turbo engines driven normally, with changes every 3,000–5,000 miles. If your owner's manual doesn't require synthetic and you drive a 2010 sedan to work and back, this is a fine choice." },
      { t: "h2", c: "Semi-Synthetic / Synthetic Blend ($64.99)" },
      { t: "p", c: "A mix of conventional and synthetic base oils. Better resistance to heat breakdown and sludge, longer intervals (typically 5,000–7,500 miles), and a noticeable step up for higher-mileage engines or vehicles that sit in Birmingham traffic every day. This is our most popular tier for good reason." },
      { t: "h2", c: "Advanced Full Synthetic ($94.99)" },
      { t: "p", c: "Engineered base oils with the most stable molecules. Required by most modern engines — especially turbocharged, direct-injection, and European vehicles — and the right choice for towing, extreme heat, or long highway trips. Intervals of 7,500–10,000 miles are common." },
      { t: "h2", c: "How to choose" },
      { t: "ul", c: [
        "Check the oil cap or owner's manual. If it says 'synthetic required,' that's not optional.",
        "Turbo? Full synthetic. Turbos run extremely hot and cook conventional oil.",
        "Over 100,000 miles on a non-turbo engine? A blend is often the sweet spot.",
        "Short trips, lots of idling, or extreme heat? Move up a tier — those conditions are harder on oil than highway miles.",
      ] },
      { t: "tip", c: "Whatever tier you choose, the filter matters as much as the oil. Every one of our oil changes includes a new filter, a multi-point inspection, and fluid top-off." },
    ],
  },
  {
    slug: "tire-rotation-balancing-alignment-difference",
    category: "tires",
    title: "Rotation, Balancing, and Alignment: What's the Difference?",
    subtitle: "Three tire services that get confused constantly — and when you need each",
    excerpt:
      "They all involve tires, they all get done on a lift, and they solve completely different problems. Here's a plain-English breakdown and the symptoms that point to each.",
    date: "2026-03-31",
    readTime: "4 min",
    author: "Auto Basic Care Service Team",
    ...img("photo-1578844251758-2f71da64c96f"),
    imageAlt: "Stack of new tires in a tire shop",
    bookingService: "Tire Rotation",
    body: [
      { t: "h2", c: "Rotation ($35)" },
      { t: "p", c: "Moving tires to different positions so they wear evenly. Front tires on most vehicles wear faster because they steer and carry the engine. Rotate every 5,000–7,500 miles — an easy habit is every other oil change. Skipping rotation is the number one reason people replace tires early." },
      { t: "h2", c: "Balancing ($75)" },
      { t: "p", c: "Adding small weights to the wheel so its mass is distributed evenly around the axle. Symptom of imbalance: a vibration that starts around 45–55 mph and gets worse as you speed up. Balancing is done whenever tires are mounted, and again if you lose a weight or notice a new vibration." },
      { t: "h2", c: "Alignment (check: $75)" },
      { t: "p", c: "Adjusting the angles of the wheels — camber, caster, toe — to factory spec so the tires roll straight and flat. Symptoms of misalignment: the car pulls to one side, the steering wheel is off-center when driving straight, or the inside or outside edge of the tread is wearing faster than the rest." },
      { t: "h2", c: "Quick symptom guide" },
      { t: "ul", c: [
        "Vibration at speed → Balancing",
        "Pulling or off-center steering wheel → Alignment",
        "One edge of the tire worn → Alignment",
        "Front tires worn much more than rear → Rotation (and probably alignment)",
        "Hit a curb or big pothole → Alignment check",
      ] },
      { t: "tip", c: "Any time steering or suspension parts are replaced — tie rods, struts, control arms — an alignment afterward is essential. Otherwise the new parts will eat your tires." },
    ],
  },
  {
    slug: "battery-alternator-or-starter",
    category: "electrical",
    title: "Battery, Alternator, or Starter? How to Tell What's Failing",
    subtitle: "Three parts, one no-start morning — and how to narrow it down",
    excerpt:
      "Click-click-click, slow crank, dimming lights, or nothing at all. Each no-start sounds different because each has a different cause. Here's how to read yours.",
    date: "2026-03-17",
    readTime: "4 min",
    author: "Auto Basic Care Service Team",
    ...img("photo-1619642751034-765dfdf7c58e"),
    imageAlt: "Technician working on electrical components in an engine bay",
    bookingService: "Battery Test",
    body: [
      { t: "p", c: "Your starting and charging system is three parts working together: the battery stores power, the starter uses it to turn the engine over, and the alternator refills the battery while you drive. When one fails, the symptoms usually tell you which." },
      { t: "h2", c: "It's probably the battery if…" },
      { t: "ul", c: [
        "You hear a rapid click-click-click and nothing else",
        "The dash lights are dim or flicker when you turn the key",
        "It starts fine after a jump — and stays running",
        "The battery is more than 3–4 years old (Alabama heat shortens battery life significantly)",
      ] },
      { t: "h2", c: "It's probably the alternator if…" },
      { t: "ul", c: [
        "The battery light is on while driving",
        "Headlights dim at idle and brighten when you rev",
        "It starts after a jump but dies again within minutes",
        "You hear a whining noise from the engine that changes with RPM",
      ] },
      { t: "h2", c: "It's probably the starter if…" },
      { t: "ul", c: [
        "Lights and radio work normally, but you get one loud click or nothing when you turn the key",
        "You hear a grinding or whirring sound but the engine doesn't turn",
        "It starts intermittently — fine one day, dead the next, with a good battery",
      ] },
      { t: "p", c: "We test all three together because they affect each other. A weak alternator kills batteries early. A failing battery makes a starter work harder. Our $25 Battery Test measures state of charge and cranking amps; Alternator Testing and Starter Testing are $65 each and include a full charging-system check." },
      { t: "tip", c: "If your battery is over three years old, have it tested before summer. Heat is harder on batteries than cold — most Birmingham batteries die in July, not January." },
    ],
  },
  {
    slug: "overheating-engine-what-to-do",
    category: "cooling",
    title: "Overheating Engine: What to Do and How to Prevent It",
    subtitle: "The 60-second response that saves engines, and the maintenance that avoids it",
    excerpt:
      "An overheating engine can go from 'a little warm' to a warped head in a few miles. Here's exactly what to do in the moment — and the cooling system care that keeps it from happening.",
    date: "2026-03-03",
    readTime: "5 min",
    author: "Auto Basic Care Service Team",
    ...img("photo-1625047509248-ec889cbff17f"),
    imageAlt: "Engine bay with the hood open during a cooling system inspection",
    bookingService: "Radiator Inspection",
    body: [
      { t: "h2", c: "If the temperature gauge climbs — do this now" },
      { t: "ul", c: [
        "Turn the AC off and the heat on full blast. The heater core pulls heat out of the coolant.",
        "Pull over as soon as it's safe. Don't try to 'make it home.'",
        "Turn the engine off. Do NOT open the radiator cap — the system is pressurized and can cause severe burns.",
        "Wait at least 30 minutes before checking coolant level in the overflow tank.",
        "If the coolant is low and you have some, add it slowly to the reservoir only once the engine is cool. Then drive gently to a shop — or call for a tow if the gauge climbs again.",
      ] },
      { t: "h2", c: "Common causes" },
      { t: "ul", c: [
        "Low coolant from a leak — hoses, water pump, radiator, or heater core",
        "Stuck thermostat — coolant can't circulate",
        "Failed cooling fan or fan relay — overheats at idle, fine on the highway",
        "Clogged radiator — years of old coolant leave deposits that block flow",
        "Failing water pump — often with a coolant smell or a whine from the front of the engine",
        "Blown head gasket — white exhaust smoke, milky oil, or constant coolant loss with no visible leak",
      ] },
      { t: "h2", c: "Prevention is cheap" },
      { t: "p", c: "Coolant doesn't last forever. Its anti-corrosion additives wear out, and old coolant becomes acidic and eats the water pump, radiator, and heater core from the inside. Most manufacturers call for a flush every 30,000–60,000 miles or every 5 years. Our $120 Cooling System Flush replaces every drop, checks the thermostat and hoses, and pressure-tests the system for leaks." },
      { t: "tip", c: "Use the exact coolant type your vehicle calls for. Mixing incompatible coolants creates a gel that clogs the radiator. The color doesn't tell you the type — check the manual." },
      { t: "p", c: "Noticed a sweet smell, a puddle under the car, or a gauge that's creeping higher than usual? Book a Radiator Inspection ($65) or a Coolant Leak Inspection ($85) before it becomes an overheating event." },
    ],
  },
];

export const articleBySlug = (slug) => articles.find((a) => a.slug === slug);
export const articlesByCategory = (cat) => articles.filter((a) => a.category === cat);
export const sortedArticles = () => [...articles].sort((a, b) => (a.date < b.date ? 1 : -1));
export const formatDate = (iso) =>
  new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
